
import { gettersInstance, parseDate } from "@/lib/util";
import { apiCall, apiMediaURL } from "@/lib/api";
import Loading from "@/components/Loading.vue";
import LicenseDescription from "@/components/PayWall/LicenseDescription.vue";
import { Component, MixedVue, mixins } from "@/vue";
import { Prop } from "vue-property-decorator";
import { ComputedLicencePlan } from "@/lib/types/misc/ComputedLicencePlan";
import { LicenseOffer } from "@/lib/types/LicenseOffer";

const StoreGetters = gettersInstance([
  "userLicense",
  "userLicenseIsTrial",
  "userLicenseIsActive",
  "currentFarmId",
  "currentFarm",
  "userFarms",
  "sharedFarms",
  "demoFarmId",
  "userLicense",
]);

@Component({
  name: "FirstStep",
  components: { LicenseDescription, Loading },
})
export default class FirstStep extends mixins(MixedVue, StoreGetters) {
  @Prop({ type: Object, required: false, default: null })
  value: ComputedLicencePlan;

  @Prop({ type: Object, required: false, default: null })
  offer: LicenseOffer;

  basePriceKey: string = "price1y";

  get farmWithCurrentSeason() {
    const atLeastOneFarm = [
      ...this.userFarms.filter((farm) => farm.current_season),
      ...this.sharedFarms.filter((farm) => farm.shared_farm === "full_access" && farm.id !== this.demoFarmId),
    ];
    return !!atLeastOneFarm.length;
  }

  get firstUserFarm() {
    return [...this.userFarms, ...this.sharedFarms]?.[0] || null;
  }

  get offerArea() {
    return this.offer?.license?.hectares;
  }

  get tosUrl() {
    return this.offer && apiMediaURL(this.offer.license.latest_tos_url);
  }

  get startDate() {
    return parseDate(this.offer?.license.start_date);
  }

  get upperBound() {
    return this.offer?.offer.upper_bound_ha;
  }
  get sharedUserNr() {
    return this.offer?.offer.shared_users_no;
  }

  get userNr() {
    // The owner is always implicitly included so +1 here
    return this.sharedUserNr + 1;
  }

  get pricingPlans() {
    return [
      {
        name: this.$gettext("standard plan"),
        priceKey: "price1y",
        choice: "1year",
        years: 1,
        // disabled: this.offer.campaign ? true : false,
      },
      {
        name: this.$gettext("special plan"),
        priceKey: "price2y",
        choice: "2year",
        years: 2,
        // disabled: true
      },
    ];
  }

  get basePlanYears() {
    const plan = this.pricingPlans.find((plan) => plan.priceKey === this.basePriceKey);
    return plan?.years;
  }

  get basePlanPrice() {
    return this.offer && parseFloat(this.offer.offer[this.basePriceKey]);
  }

  get availablePlans(): ComputedLicencePlan[] {
    if (!this.offer) return [];

    return this.pricingPlans.map((plan) => {
      const period = this.$gettextInterpolate(this.$ngettext("%{ n } year", "%{ n } years", plan.years), { n: plan.years });
      const price = parseFloat(this.offer.offer[plan.priceKey]);
      const priceWithVat = parseFloat(this.offer.offer[`${plan.priceKey}_with_vat`]);
      const savings = (plan.years / this.basePlanYears) * this.basePlanPrice - price;

      const startDate = new Date();
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + plan.years);

      return {
        ...plan,
        key: `${this.userLicense.id}-${plan.priceKey}`,
        price,
        priceWithVat,
        period,
        savings,
        startDate,
        endDate,
        offer: this.offer.offer,
        campaign: this.offer.campaign,
        colorClass: savings > 0 ? "brand-green-text" : "muted",
        upperBound: this.upperBound,
        tosUrl: this.tosUrl,
        userNr: this.userNr,
      };
    });
  }

  async uploadMore() {
    this.$emit("close");
    await this.$router.push({
      name: "manage-farm",
      params: {
        farmId: this.currentFarmId,
      },
    });
  }

  async maybeStartTrial() {
    const licenseId = this.userLicense.id;
    const farmId = this.firstUserFarm.id;

    if (!this.userLicense?.id || !this.firstUserFarm?.id) {
      throw new Error("User license ID and farm ID are required");
    }
    if (this.farmWithCurrentSeason) {
      await this.$router.push({
        name: "alcedo-order",
        params: { farmId: this.firstUserFarm.id },
      });
      return;
    }

    await this.activateTrial(licenseId);
    this.$emit("close");
    await this.$router.push({
      name: "manage-farm",
      params: { farmId },
    });
  }

  async activateTrial(licenseId: string) {
    try {
      await apiCall("POST", `/license/${licenseId}/start_alcedo_trial/`);
      await apiCall("POST", `/license/${licenseId}/later/`);
      await this.$store.dispatch("getCurrentUser");
    } catch (error) {
      console.error("Trial activation failed:", error);
      this.notify({
        type: "error",
        text: this.$gettext("Trial activation failed.") + " " + error.message,
      });
      throw error;
    }
  }

  selectPlan(plan: ComputedLicencePlan) {
    if (plan.disabled) return;

    this.$emit("input", plan);
  }
}
